import React, { useState, useEffect } from 'react';
import { Eye, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCustomRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const limit = 20;

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await axios.get(`${API}/admin/custom-car-requests`, { params, withCredentials: true });
      setRequests(response.data.requests);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [statusFilter, page]);

  const updateStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/admin/custom-car-requests/${requestId}/status?status=${newStatus}`, {}, { withCredentials: true });
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const productNames = { gloss: 'Whole Body Gloss', matt: 'Whole Body Matt', coloured: 'Whole Body Coloured', essential: 'Essential Package' };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#F8F8F8]">Custom Car Requests</h1>
          <p className="text-[#A0A0A0] mt-1">{total} total requests</p>
        </div>
        <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(0); }}>
          <SelectTrigger className="w-[180px] bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] rounded-none">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#333333]">
            <SelectItem value="all" className="text-[#F8F8F8]">All Requests</SelectItem>
            <SelectItem value="new" className="text-[#F8F8F8]">New</SelectItem>
            <SelectItem value="contacted" className="text-[#F8F8F8]">Contacted</SelectItem>
            <SelectItem value="quoted" className="text-[#F8F8F8]">Quoted</SelectItem>
            <SelectItem value="completed" className="text-[#F8F8F8]">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#111111] border border-[#222222]">
        {loading ? (
          <div className="p-8 text-center text-[#A0A0A0]">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center text-[#A0A0A0]">No custom requests found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#222222] bg-[#0A0A0A]">
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Customer</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Vehicle</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Product</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Status</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Date</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id} className="border-b border-[#222222] hover:bg-[#1A1A1A]">
                    <td className="py-3 px-4">
                      <p className="text-[#F8F8F8] text-sm">{request.name}</p>
                      <p className="text-[#666666] text-xs">{request.email}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-[#F8F8F8] text-sm">{request.car_year} {request.car_brand} {request.car_model}</p>
                      <p className="text-[#666666] text-xs uppercase">{request.body_type}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-[#F8F8F8] text-sm">{productNames[request.product_type] || request.product_type}</p>
                      {request.colour && <p className="text-[#00F0FF] text-xs">{request.colour}</p>}
                    </td>
                    <td className="py-3 px-4">
                      <Select value={request.status} onValueChange={(value) => updateStatus(request.id, value)}>
                        <SelectTrigger className={`w-[120px] h-8 text-xs rounded-none border-0 ${
                          request.status === 'new' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                          request.status === 'contacted' ? 'bg-[#FFCC00]/20 text-[#FFCC00]' :
                          request.status === 'quoted' ? 'bg-[#00F0FF]/20 text-[#00F0FF]' :
                          'bg-[#34C759]/20 text-[#34C759]'
                        }`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                          <SelectItem value="new" className="text-[#F8F8F8]">New</SelectItem>
                          <SelectItem value="contacted" className="text-[#F8F8F8]">Contacted</SelectItem>
                          <SelectItem value="quoted" className="text-[#F8F8F8]">Quoted</SelectItem>
                          <SelectItem value="completed" className="text-[#F8F8F8]">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4 text-[#A0A0A0] text-sm">{formatDate(request.created_at)}</td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(request)} className="text-[#A0A0A0] hover:text-[#F8F8F8]">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[#222222]">
            <p className="text-[#A0A0A0] text-sm">Page {page + 1} of {totalPages}</p>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="border-[#333333] text-[#A0A0A0] hover:bg-[#1A1A1A] rounded-none">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="border-[#333333] text-[#A0A0A0] hover:bg-[#1A1A1A] rounded-none">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="bg-[#111111] border-[#222222] text-[#F8F8F8] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Custom Request Details</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="bg-[#1A1A1A] p-4 border border-[#222222]">
                <p className="text-[#A0A0A0] text-sm">Customer</p>
                <p className="text-[#F8F8F8] font-medium">{selectedRequest.name}</p>
                <p className="text-[#00F0FF] text-sm">{selectedRequest.email}</p>
                <p className="text-[#A0A0A0] text-sm">{selectedRequest.phone}</p>
              </div>

              <div className="bg-[#1A1A1A] p-4 border border-[#222222]">
                <p className="text-[#A0A0A0] text-sm">Vehicle</p>
                <p className="text-[#F8F8F8] font-medium">{selectedRequest.car_year} {selectedRequest.car_brand} {selectedRequest.car_model}</p>
                <p className="text-[#666666] text-sm uppercase">{selectedRequest.body_type}</p>
              </div>

              <div className="bg-[#1A1A1A] p-4 border border-[#222222]">
                <p className="text-[#A0A0A0] text-sm">Product Requested</p>
                <p className="text-[#F8F8F8] font-medium">{productNames[selectedRequest.product_type] || selectedRequest.product_type}</p>
                {selectedRequest.colour && <p className="text-[#00F0FF] text-sm">Colour: {selectedRequest.colour}</p>}
                {selectedRequest.include_installation_kit && <p className="text-[#A0A0A0] text-sm">+ Installation Kit</p>}
              </div>

              {selectedRequest.message && (
                <div className="bg-[#1A1A1A] p-4 border border-[#222222]">
                  <p className="text-[#A0A0A0] text-sm">Additional Notes</p>
                  <p className="text-[#F8F8F8] mt-2">{selectedRequest.message}</p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-[#222222]">
                <p className="text-[#666666] text-sm">{formatDate(selectedRequest.created_at)}</p>
                <Button onClick={() => updateStatus(selectedRequest.id, 'contacted')}
                  className="bg-[#00F0FF] text-[#050505] hover:bg-[#33F3FF] rounded-none">
                  <Check className="w-4 h-4 mr-2" /> Mark as Contacted
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomRequestsPage;
